import React, { useCallback, useEffect, useState } from "react";
import { Star, AlertTriangle, Save, Send } from "lucide-react";
import { PageHeader, Button, Input, Textarea, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type ReviewConfig, type ReviewRequest, type ReviewMetrics } from "@/lib/api";

export default function Reviews() {
  const [config, setConfig] = useState<ReviewConfig | null>(null);
  const [metrics, setMetrics] = useState<ReviewMetrics | null>(null);
  const [rows, setRows] = useState<ReviewRequest[] | null>(null);
  const [form, setForm] = useState({ google_review_url: "", message: "", delay_hours: 2 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.reviewsList().then(setRows).catch(() => {});
    api.reviewsMetrics().then(setMetrics).catch(() => {});
  }, []);
  useEffect(() => {
    api.reviewsConfig().then((c) => { setConfig(c); setForm({ google_review_url: c.google_review_url ?? "", message: c.message, delay_hours: c.delay_hours }); }).catch(() => {});
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try { await api.reviewsConfigSet({ ...form, delay_hours: Number(form.delay_hours) }); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const runNow = async () => {
    try { const r = await api.reviewsProcess(); toast.success(`Processed ${r.scanned} request(s)`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cards = metrics ? [
    { label: "Requests sent", value: metrics.requests_sent },
    { label: "Review-link clicks", value: metrics.review_link_clicks },
    { label: "Feedback received", value: metrics.feedback_received },
  ] : [];

  return (
    <div className="space-y-6" data-testid="page-reviews">
      <PageHeader title="Reputation & Reviews" description="Automatically ask happy customers for a review after a completed job." testid="reviews-header"
        action={<Button variant="outline" onClick={runNow} data-testid="reviews-process-btn"><Send className="h-4 w-4" /> Process queue</Button>} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="reviews-config-status">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          {config.sequences ? <StatusBadge tone="green">Automated sequences</StatusBadge> : <StatusBadge tone="slate">Basic request</StatusBadge>}
          {config.analytics && <StatusBadge tone="green">Reputation analytics</StatusBadge>}
          {config.sms_configured ? <StatusBadge tone="green">SMS live</StatusBadge>
            : <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> SMS: configuration required</StatusBadge>}
        </div>
      )}
      {config && !config.sms_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="reviews-honest-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">Requests queue automatically after completed jobs, but SMS sending is paused until Twilio is live — due requests are marked <b>“Unavailable — Configuration Required”</b> and never recorded as sent. We never claim a review was posted; we only track link clicks.</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3" data-testid="reviews-metrics">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium text-slate-500">{c.label}</div>
            <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{c.value}</div>
          </div>
        ))}
      </div>

      <Card><CardContent className="space-y-3 p-4" data-testid="reviews-settings">
        <h2 className="text-sm font-semibold text-slate-800">Review request settings</h2>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Google review URL</p>
          <Input value={form.google_review_url} onChange={(e) => setForm({ ...form, google_review_url: e.target.value })} placeholder="https://g.page/r/…/review" data-testid="reviews-google-url" /></div>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Approved message <span className="text-slate-400">(use {"{first_name}"}, {"{business}"}, {"{link}"})</span></p>
          <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} data-testid="reviews-message" /></div>
        <div className="w-48"><p className="mb-1 text-xs font-medium text-slate-500">Delay after service (hours)</p>
          <Input type="number" min="0" value={form.delay_hours} onChange={(e) => setForm({ ...form, delay_hours: Number(e.target.value) })} data-testid="reviews-delay" /></div>
        <Button onClick={save} disabled={busy} data-testid="reviews-save-btn"><Save className="h-4 w-4" /> Save settings</Button>
      </CardContent></Card>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-800">Recent requests</h2>
        {rows === null ? <LoadingBlock rows={3} /> : rows.length === 0 ? (
          <EmptyState icon={Star} title="No review requests yet" description="Mark an appointment complete to queue a review request." />
        ) : (
          <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="reviews-list">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-4 py-3" data-testid={`review-row-${r.id}`}>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <StatusBadge tone={r.status === "sent" ? "green" : r.status === "unavailable" ? "amber" : r.status === "feedback_received" ? "blue" : "slate"}>{r.status === "unavailable" ? "Unavailable — Config Required" : r.status.replace(/_/g, " ")}</StatusBadge>
                  {r.clicked_at && <span className="text-xs text-emerald-600">link clicked</span>}
                </div>
                {r.feedback && <span className="flex items-center gap-0.5 text-xs text-amber-600">{Array.from({ length: r.feedback.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}</span>}
              </div>
            ))}
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
